import React, { useEffect, useRef } from "react";
import { select, scaleLinear } from "d3";

export default function Likert({ datasets, valueType, LikertScaleType, LikertScale }) {
  const svgRef = useRef();
  const wrapperRef = useRef();

  // Function to generate color array based on the number of segments
  const generateColors = (numSegments, type) => {
    let baseColors = [
      [201, 4, 3, 1.0], // Start color
      [229, 85, 85, 1.0],
      [237, 125, 125, 1.0],
      [166, 166, 166, 1.0], // Middle color
      [80, 131, 204, 1.0],
      [35, 97, 161, 1.0],
      [22, 62, 102, 1.0], // End color
    ];

    if (type === 'matrix' || type === 'custom') {
      baseColors = [
        [214, 40, 40, 1.0],    // 1 - Deep Red
        [230, 111, 30, 1.0],   // 2 - Burnt Orange
        [241, 169, 30, 1.0],   // 3 - Amber Yellow
        [180, 200, 40, 1.0],   // 4 - Yellow-Green
        [56, 161, 79, 1.0],    // 5 - Emerald Green
        [30, 185, 170, 1.0],   // 6 - Teal
        [52, 143, 210, 1.0],   // 7 - Sky Blue
        [63, 84, 186, 1.0],    // 8 - Indigo
        [128, 52, 180, 1.0],   // 9 - Purple
        [196, 50, 140, 1.0],   // 10 - Magenta
      ];

      if (numSegments === 5) {
      return [
        `rgba(${baseColors[0].join(",")})`,
        `rgba(${baseColors[1].join(",")})`,
        `rgba(${baseColors[2].join(",")})`, 
        `rgba(${baseColors[3].join(",")})`,
        `rgba(${baseColors[4].join(",")})`,
        ];
      } else if (numSegments === 7) {
        return [
        `rgba(${baseColors[0].join(",")})`,
        `rgba(${baseColors[1].join(",")})`,
        `rgba(${baseColors[2].join(",")})`,
        `rgba(${baseColors[3].join(",")})`,
        `rgba(${baseColors[4].join(",")})`,
        `rgba(${baseColors[5].join(",")})`,
        `rgba(${baseColors[6].join(",")})`,
        ];
      } else if (numSegments === 10) {
        return baseColors.map((color) => `rgba(${color.join(",")})`);
      }
      return baseColors.map((color) => `rgba(${color.join(",")})`); // Default to 7 colors
    }

    // Map the base colors to the desired number of segments
    if (numSegments === 5) {
      return [
        `rgba(${baseColors[0].join(",")})`,
        `rgba(${baseColors[1].join(",")})`,
        `rgba(${baseColors[3].join(",")})`, // Middle color
        `rgba(${baseColors[5].join(",")})`,
        `rgba(${baseColors[6].join(",")})`,
      ];
    } else if (numSegments === 7) {
      return baseColors.map((color) => `rgba(${color.join(",")})`);
    } else if (numSegments === 10) {
      // Interpolate additional colors for 10 segments
      return [
        `rgba(${baseColors[0].join(",")})`,
        `rgba(${interpolateColor(baseColors[0], baseColors[1], 0.5).join(",")})`,
        `rgba(${baseColors[1].join(",")})`,
        `rgba(${interpolateColor(baseColors[1], baseColors[2], 0.5).join(",")})`,
        `rgba(${baseColors[2].join(",")})`,
        `rgba(${baseColors[3].join(",")})`, // Middle color
        `rgba(${baseColors[4].join(",")})`,
        `rgba(${interpolateColor(baseColors[4], baseColors[5], 0.5).join(",")})`,
        `rgba(${baseColors[5].join(",")})`,
        `rgba(${baseColors[6].join(",")})`,
      ];
    }
    return baseColors.map((color) => `rgba(${color.join(",")})`); // Default to 7 colors
  };

  // Helper function to interpolate between two colors
  const interpolateColor = (color1, color2, factor) => {
    return color1.map((channel, index) =>
      Math.round(channel + factor * (color2[index] - channel))
    );
  };

  // Determine the number of segments based on the dataset or LikertScale if custom
  let numSegments = 5;
  if (LikertScale) {
    try {
      if (typeof LikertScale === 'string') {
        if (LikertScale.trim().startsWith('[') || LikertScale.trim().startsWith('{')) {
          numSegments = JSON.parse(LikertScale).length;
        } else {
          numSegments = LikertScale.split(',').length;
        }
      } else if (Array.isArray(LikertScale)) {
        numSegments = LikertScale.length;
      }
    } catch (e) {
      console.error("Error parsing Scale size in Likert", e);
      if (typeof LikertScale === 'string') numSegments = LikertScale.split(',').length;
    }
  } else {
    numSegments = Object.keys(datasets).length - 3; // Subtract 'id', 'choiceName', and 'mean'
  }
  const colors = generateColors(numSegments, LikertScaleType);

  useEffect(() => {
    const svg = select(svgRef.current);
    const { width } = wrapperRef.current.getBoundingClientRect();

    let data = [];
    if (LikertScaleType === 'matrix' || LikertScaleType === 'custom' || LikertScaleType === 'Custom Likert' || LikertScale) {
      for (let i = 1; i <= numSegments; i++) {
        data.push(parseInt(datasets[`ScaleNo_${i}`] || 0));
      }
    } else {
      let dk = Object.keys(datasets);
      for (let i = 0; i < dk.length; i++) {
        if (dk[i] !== "id" && dk[i] !== "choiceName" && dk[i] !== "mean") {
          data.push(parseInt(datasets[dk[i]]));
        }
      }
    }

    // let dataTemp = [];
    // if (valueType === "%") {
    //   const sum = data.reduce((a, b) => a + b, 0);
    //   dataTemp = data.map((value) => Math.floor((value / sum) * 100));
    // } else {
    //   dataTemp = data;
    // }
    let dataTemp = [];

    if (valueType === "%") {
      const sum = data.reduce((a, b) => a + b, 0);
      let rawPercents = data.map((value) => (value / sum) * 100);
      let roundedPercents = rawPercents.map((val) => Math.round(val));
      let diff = 100 - roundedPercents.reduce((a, b) => a + b, 0);

      // Adjust last element to ensure total is 100%
      if (roundedPercents.length > 0) {
        roundedPercents[roundedPercents.length - 1] += diff;
      }

      dataTemp = roundedPercents;
    } else {
      dataTemp = data;
    }

    data = dataTemp;

    const total = data.reduce((acc, val) => acc + val, 0);

    svg.selectAll("*").remove();

    svg
      .append("g")
      .selectAll("rect")
      .data(data)
      .join("rect")
      .attr("fill", (d, i) => colors[i])
      .attr("class", "barreact")
      .attr("x", (d, i) => {
        if (i === 0) return 0;
        let prevSum = data.slice(0, i).reduce((acc, val) => acc + val, 0);
        return total > 0 ? (width * prevSum) / total : 0;
      })
      .attr("width", (d) => (total > 0 ? (width * d) / total : 0))
      .attr("y", (LikertScaleType === 'matrix' || LikertScaleType === 'custom' || LikertScaleType === 'Custom Likert') && LikertScale ? 15 : 0) // Move down if matrix
      .attr("height", 30); // Always full height

    const gr = svg.selectAll("rect");
    const ngroup = svg.append("g");
    gr.each(function (p, j) {
      ngroup
        .append("text")
        .text(p !== 0 ? p : "")
        .attr("x", () => {
          let x = 0;
          if (j !== 0) {
            let prevSum = data.slice(0, j).reduce((acc, val) => acc + val, 0);
            x = total > 0 ? (width * prevSum) / total : 0;
          }
          return x + (total > 0 ? (width * p) / total / 2 : 0);
        })
        .attr("y", (LikertScaleType === 'matrix' || LikertScaleType === 'custom' || LikertScaleType === 'Custom Likert') && LikertScale ? 33 : 18) // Adjust center text position
        .attr("text-anchor", "middle")
        .attr("font-family", "Noto Sans")
        .attr("font-size", "11px")
        .attr("fill", "white");
    });
    // Add labels above bars for custom Likert scale
    // if (LikertScaleType === 'custom' && LikertScale) {
    //   let labels = [];
    //   try {
    //     if (typeof LikertScale === 'string') {
    //       // Check if it's a JSON string first (just in case)
    //       if (LikertScale.trim().startsWith('[') || LikertScale.trim().startsWith('{')) {
    //         labels = JSON.parse(LikertScale);
    //       } else {
    //         // Otherwise treat as comma separated string
    //         labels = LikertScale.split(',');
    //       }
    //     } else {
    //       labels = LikertScale;
    //     }
    //   } catch (e) {
    //     console.error("Error parsing LikertScale", e);
    //     // Fallback to split if JSON parse fails but it is a string
    //     if (typeof LikertScale === 'string') {
    //       labels = LikertScale.split(',');
    //     }
    //   }

    //   const total = data.reduce((acc, val) => acc + val, 0);

    //   if (Array.isArray(labels)) {
    //     svg.selectAll(".custom-label").remove(); // Clear previous labels

    //     data.forEach((val, i) => {
    //       // Calculate x position relative to the bar
    //       let prevSum = data.slice(0, i).reduce((acc, v) => acc + v, 0);
    //       let xPos = total > 0 ? (width * prevSum) / total : 0;
    //       let barWidth = total > 0 ? (width * val) / total : 0;

    //       if (val > 0) {
    //         svg.append("text")
    //           .attr("class", "custom-label")
    //           .attr("y", 10) // Position inside the top of the bar (since we can't go above with current SVG height)
    //           .attr("x", xPos + barWidth / 2) // Center in the bar
    //           .text(labels[i]?.questionLikertScaleName || labels[i]?.LikertScale || labels[i]?.scaleName || labels[i]?.option || labels[i]?.label || labels[i] || "")
    //           .attr("text-anchor", "middle")
    //           .attr("font-size", "10px")
    //           .attr("fill", "#38454F");
    //       }
    //     });
    //   }
    // }
  }, [datasets, valueType, colors, LikertScaleType, LikertScale]);


  return (
    <div
      ref={wrapperRef}
      style={{ width: "100%", height: (LikertScaleType === 'matrix' || LikertScaleType === 'custom' || LikertScaleType === 'Custom Likert') && LikertScale ? "45px" : "30px", marginBottom: "1rem" }}
    >
      <svg ref={svgRef} style={{ width: "100%", height: "110%" }}>
        <g className="x-axis" />
        <g className="y-axis" />
        <g className="content" />
      </svg>
    </div>
  );
}